import React, { useEffect, useState } from 'react'
import styled from 'styled-components';
import IconButton from '../GenericComponents/IconButton';
import { ReactComponent as Meeting } from "../../Assets/iconsv2/sidebar/Meetings.svg";

import {
    autoUpdate,
    flip,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from "@floating-ui/react";

import { useMeetingsService } from '../../Context/MeetingContext';

import NewMeetingDetails from './NewMeetingDetails';
import AddMeetingMembers from './AddMeetingMembers';

import { useTranslate } from '@tolgee/react';
import { ACCESS_LEVEL_PLATFORM } from '../../Constants/constantValues';
import { useGenericComponentsStateService } from '../../Context/GenericComponentsStateContext';
import { useNotify } from '../../Helpers/Notifications';
import { getApiErrorMessage } from '../../Helpers/apiErrorMessage';
import { createDefaultRecurrencePattern, patternToApiParams } from '../../Helpers/recurrenceHelper';

const AddNewMeetingModal = ({
    contacts,
    loadMeetings,
    checkSubscriptionAndExecute,
}) => {
    const { t } = useTranslate();
    const { notifySuccess, notifyError, notifyInfo } = useNotify();
    const { isAddNewMeetingOpen, setIsAddNewMeetingOpen } = useGenericComponentsStateService();
    const { setLoading } = useGenericComponentsStateService()

    const { createMeeting, createRecurringMeeting } = useMeetingsService();
    const [idempotencyKey] = useState(() => crypto.randomUUID());

    const [selectedContacts, setSelectedContacs] = useState([]);
    const [initialContacts, setInitialContacts] = useState([]);

    const [otherParticipants, setOtherParticipants] = useState([]);
    const [initialOtherParticipants, setInitialOtherParticipants] = useState([]);


    const [addParticipants, setAddParticipants] = useState(false)
    const initialValues = {
        meetingTitle: "",
        meetingDetails: "",
    };

    const [inputValues, setInputValues] = useState(initialValues);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getNextHalfHour = () => {
        const now = new Date();
        const minutes = now.getMinutes();
        const roundedMinutes = minutes < 30 ? 30 : 60;

        now.setMinutes(roundedMinutes, 0, 0); // Set minutes to 30 or 60, and reset seconds & ms

        return now;
    };

    const meetingStart = getNextHalfHour();
    const meetingEnd = new Date(meetingStart.getTime() + 60 * 60 * 1000);

    const [startDate, setStartDate] = useState(meetingStart);
    const [endDate, setEndDate] = useState(meetingEnd);
    const [recurrencePattern, setRecurrencePattern] = useState(() => createDefaultRecurrencePattern(meetingStart));
    const handleInputChange = (e) => {
        const { name, value } = e.currentTarget;
        setInputValues({
            ...inputValues,
            [name]: value,
        });
    };

    const handleSaveBtn = () => {
        setInitialContacts(selectedContacts);
        setInitialOtherParticipants(otherParticipants);
        setAddParticipants(false);
    };

    const handleBackBtn = () => {
        setSelectedContacs(initialContacts);
        setOtherParticipants(initialOtherParticipants);
        setAddParticipants(false);
    };

    const handleSelectContact = (contact) => {
        let found = selectedContacts.find((item) => item.id === contact.id);
        if (found) {
            let filtered = selectedContacts.filter((item) => item.id != contact.id);
            setSelectedContacs(filtered);
        } else {
            setSelectedContacs((prev) => {
                let newList = [...prev, contact];
                let sorted = newList.sort((a, b) => (a.name > b.name ? 1 : -1));
                return sorted;
            });
        }
    };

    const handleCreateMeetingSuccess = () => {
        setIsAddNewMeetingOpen(false)
        setLoading(false)
        setIsSubmitting(false)
        loadMeetings();
    };

    const handleCreateMeetingError = (error) => {
        setLoading(false)
        setIsSubmitting(false)
        notifyError(getApiErrorMessage(error, t, {
            errorCodeMap: {
                ACCESS1005: 'meetings.plan_or_users_error',
            },
            validationMessageMap: {
                "Title can't be blank": 'general.error_title_required',
                'must be after the start time': 'general.error_end_before_start',
                'do not exist': 'general.error_invalid_participants',
            },
        }));
    };

    const createNewMeeting = () => {
        setLoading(true)
        const participantIds = selectedContacts.map((contact) => contact.id);

        if (recurrencePattern.frequency === "none") {
            const dataObject = {
                title: inputValues.meetingTitle,
                description: inputValues.meetingDetails,
                start_at: startDate.toISOString(),
                end_at: endDate.toISOString(),
                participants_ids: [],
                idempotency_key: idempotencyKey,
            };

            if (participantIds.length > 0) {
                dataObject.participants_ids = participantIds;
            }

            if (otherParticipants.length > 0) {
                dataObject.other_participants = otherParticipants;
            }

            createMeeting(dataObject, handleCreateMeetingSuccess, handleCreateMeetingError);
            return;
        }

        const dataObject = {
            title: inputValues.meetingTitle,
            description: inputValues.meetingDetails,
            start_at: startDate.toISOString(),
            end_at: endDate.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            participants_ids: participantIds,
            other_participants: otherParticipants,
            ...patternToApiParams(recurrencePattern),
        };

        createRecurringMeeting(dataObject, handleCreateMeetingSuccess, handleCreateMeetingError);
    }
    const handleCreateNewMeeting = () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const accessLevel = ACCESS_LEVEL_PLATFORM;
        checkSubscriptionAndExecute(accessLevel, () => {
            createNewMeeting();
        }, () => {
            setIsSubmitting(false);
        });
    };

    const handleCloseAddNewMeeting = () => {
        setIsAddNewMeetingOpen(false);
        setSelectedContacs([])
        setInitialContacts([])
        setOtherParticipants([])
        setInitialOtherParticipants([])
        setAddParticipants(false);
        setInputValues(initialValues)
        setStartDate(meetingStart);
        setEndDate(meetingEnd)
        setRecurrencePattern(createDefaultRecurrencePattern(meetingStart));
    }

    useEffect(() => {
        // Create new Date objects representing only the date part of the start and end dates
        const startDateCheck = new Date(startDate);
        startDateCheck.setHours(0, 0, 0, 0); // Set time to 00:00:00 for comparison

        const endDateCheck = new Date(endDate);
        endDateCheck.setHours(0, 0, 0, 0); // Set time to 00:00:00 for comparison

        const startTime = startDate.getTime(); // Keep the full start date and time
        const endTime = endDate.getTime(); // Keep the full end date and time

        if (endDateCheck < startDateCheck) {
            setEndDate(new Date(startDate));
        }

        // Case 2: If dates are the same but end time is earlier than start time, add a buffer
        if (endDateCheck.getTime() === startDateCheck.getTime() && endTime <= startTime) {
            const updatedEndDate = new Date(startDate);
            updatedEndDate.setMinutes(startDate.getMinutes() + 5); // Add a 5-minute buffer
            setEndDate(updatedEndDate);
        }

    }, [startDate, endDate]);


    return (
        <Container>
            {addParticipants &&
                <AddMeetingMembers
                    contacts={contacts}
                    selectedContacts={selectedContacts}
                    setSelectedContacs={setSelectedContacs}
                    initialContacts={initialContacts}
                    handleSelectContact={handleSelectContact}
                    handleSaveBtn={handleSaveBtn}
                    handleBackBtn={handleBackBtn}
                    otherParticipants={otherParticipants}
                    setOtherParticipants={setOtherParticipants}
                    initialOtherParticipants={initialOtherParticipants}
                    setInitialOtherParticipants={setInitialOtherParticipants}
                />}

            <MenuContainer>
                <NewMeetingDetails
                    handleInputChange={handleInputChange}
                    inputValues={inputValues}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    recurrencePattern={recurrencePattern}
                    setRecurrencePattern={setRecurrencePattern}
                    selectedContacts={selectedContacts}
                    setAddParticipants={setAddParticipants}
                    handleCreateNewMeeting={handleCreateNewMeeting}
                    isSubmitting={isSubmitting}
                    otherParticipants={otherParticipants}
                    handleCloseAddNewMeeting={handleCloseAddNewMeeting}
                />
            </MenuContainer>

        </Container>
    )
}

export default AddNewMeetingModal


const Container = styled.div`
 background: ${({ theme }) => theme.chat.conversationBackground};
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  margin:auto;
  overflow: auto;
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.background};
height:100%;
overflow:hidden;
width:100%;
  gap: 16px;
`;